<template>
  <div class="section" :class="main_color">
    <div class="part part-avis-generaux">
      <div class="tle-row">
        <div class="tle-col small-half">
          <div class="q-mb-lg">
            <p class="label main_color_text">Notation des critères d'analyse</p>

            <div v-for="row in avis_rows" class="avis-row" :key="row.property">
              <p>{{ row.label }}</p>
              <el-progress-slider
                :parent_data="active_study.etudeData.synthese"
                :property="row.property"
                :max="10"
                :editable="true"
              />
            </div>
          </div>
          <p class="label main_color_text">
            Hypothèses de prix de vente du programme
            <span class="small">(TVA 20%)</span>
          </p>
          <el-table :table="table_prix_vente" variant="tags" />
        </div>
        <div class="tle-col big-half">
          <information title="Niveau de priorité de la commune ou du quartier pour du LLI" class="q-mb-md q-mt-xs fixed-margin">
            <div class="priorite">
              <el-textarea
				:allow_edit="false"
                :parent_data="priorite.content.commune[0].data_list[0]"
                property="value"
                :editable="priorite.content.commune[0].data_list[0].edit"
              />
            </div>
          </information>

          <p class="label main_color_text">Positionnement locatif et prix de vente</p>

          <div class="box-edit righter first" :class="{'error': total_lines > 12}" v-show="editor.mode == 'editing'">
            <div class="error-message">
              Il y a trop de tableaux ou de lignes. Des éléments risquent de dépasser de la page.
            </div>
            <div class="box-edit-line">
              <label>Nombre de tableaux</label>
              <input type="range" v-model="nb_tables" min="1" max="3" step="1" />
              <span class="value">{{ nb_tables }}</span>
            </div>
            <div v-for="line in [1, 2, 3]" :key="line">
              <div v-if="line == 1 || nb_tables >= line">
                <div class="q-ml-md q-mt-sm">Tableau {{ line }}</div>
                <div class="box-edit-line">
                  <label>Nombre de lignes</label>
                  <input
                    type="range"
                    :value="location_lines(line)"
                    @input="set_location_line(line, $event.target.value)"
                    min="1" max="9" step="1" />
                  <span class="value">{{ location_lines(line) }} lignes</span>
                </div>
                <div class="box-edit-line">
                  <label>Types de stationnement (3 max)</label>
                  <ul>
                    <li
                      v-for="parking in active_study.etudeData.synthese[`stationnements_${line}`]"
                      :key="parking.id"
                    >
                      <el-checkbox-number :parent_data="parking" editable="true" :disabled="parking.checked == false && active_study.etudeData.synthese[`stationnements_${line}`].filter(stat => stat.checked == true).length >= 3" />
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="box-edit-line">
              <label>Configuration</label>
              <ul>
                <li>
                  <el-checkbox label="Afficher prix de vente individuel avec stat." :parent_data="active_study.etudeData.synthese" property="afficher_individuel_avec_stat" editable="true" />
                </li>
              </ul>
            </div>
          </div>

          <div v-for="line in [1, 2, 3]" :key="line">
            <div class="q-mb-sm" v-show="line == 1 || nb_tables >= line">
              <el-table :table="table_synthese_locatif(line)" variant="highlight-results" />
              <p
                class="source"
              >{{ parking_synthesis(active_study.etudeData.synthese[`stationnements_${line}`]) }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="tle-row">
        <div class="tle-col" :class="{'half': showEvolutions}">
          <p class="label main_color_text">Recommandations</p>
          <div class="comment">
            <el-textarea
              :parent_data="active_study.etudeData.synthese"
              property="commentaire"
              :editable="true"
              height='160px'
              overflow_edit='hidden scroll'
            />
          </div>
        </div>
        <div v-if="showEvolutions" class="tle-col half">
          <el-closed-question
            :parent_data="active_study.etudeData.synthese"
            property="evo_loyer_preco_vs_fiche_initiale"
            :editable="true"
            :dense="true"
            label="Evo. du loyer préconisé vs fiche initiale"
          >
          <div  v-if="active_study.etudeData.synthese.evo_loyer_preco_vs_fiche_initiale" @click.stop style="display: flex">
            <el-textarea
              placeholder="X"
              :parent_data="active_study.etudeData.synthese"
              property="evo_loyer_preco_percent"
              :editable="true"
              :submit_on_enter_key="true"
            />
            <span>
              %
            </span>
          </div>
          </el-closed-question>
          <div v-if="active_study.etudeData.synthese.evo_loyer_preco_vs_fiche_initiale">
            <div class="q-ml-md q-my-sm">
              Les raisons de l'évolution :
            </div>
            <el-closed-question
              v-for="row in evolutions_rows"
              :key="row.property"
              :parent_data="active_study.etudeData.synthese"
              :property="row.property"
              :editable="true"
              :dense="true"
              :label="row.label"
              :allowNC="row.allowNC"
            />
          </div>
		</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PageMixin from "@/components/study/editor/pages/page.mixin.js";
import number from "@/libs/number";
import Information from "@/components/global/information";
import _ from 'lodash';

export default {
	name: "section-synthese",
	mixins: [PageMixin],
	data: () => ({
		lock: false,
		avis_rows: [
			{
				label: "Attractivité du site du projet",
				property: "dynamisme_attractivite"
			},
			{
				label: "Potentiel pour le marché locatif libre",
				property: "potentiel_locatif_libre"
			},
			{
				label: "Potentiel pour le marché locatif intermédiaire",
				property: "potentiel_locatif_intermediraire"
			},
			{
				label: "Situation du marché de la promotion",
				property: "situation_promotion"
			},
			{
				label: "Valorisation patrimoniale à 10 ans",
				property: "valorisation_patrimoniale"
			},
			{
				label: "SYNTHESE GENERALE : commercialité du projet",
				property: "synthese_generale"
			}
		],
		evolutions_rows: [
			{
				property: "evo_loyer_marche",
				label: "Evo. des loyers sur le marché",
				allowNC: false
			},
			{
				property: "evo_nb_logts_envisages",
				label: "Evo. du nb de logts envisagés",
				allowNC: false
			},
			{
				property: "evo_granulométrie_surf_hab_moy",
				label: "Evo. de la granulométrie et surf. hab. moy.",
				allowNC: false
			},
			{
				property: "evo_montant_charges_loc",
				label: "Evo. du montant estimé des charges locatives",
				allowNC: true
			},
		]
	}),
	props: {
		main_color: {
			default: "dark_red",
			type: String
		}
	},
	computed: {
		...mapGetters(["active_study", "editor"]),
		priorite: function () {
			const component = this.active_study.qlikData.component.find((component) => {
				return component.name == "niveau priorite" || component.id == "1.1";
			});
			return component;
		},
		total_lines: function () {
			let val = 0;
			for (let i = 1 ; i <= this.nb_tables ; i++) {
				const table = this.table_synthese_locatif(i);
				val += table.body.length;
			}
			return val;
		},
		nb_tables: {
			get () {
				return parseInt(
					this.active_study.etudeData.synthese.nb_location_tables
				);
			},
			set (value) {
				this.$store.dispatch("edit_object", {
					obj: this.active_study.etudeData.synthese,
					field: "nb_location_tables",
					value: parseInt(value)
				});
			}
		},
		location_lines () {
			return (line) => parseInt(this.active_study.etudeData.synthese[`location_${line}_lines`]);
		},
		table_prix_vente () {
			const table = {
				head: [],
				body: [],
				config: {
					cols_width: [60, 40]
				}
			};
			table.body.push([
				{ value: "Collectif sans stat." },
				{
					parent_data: this.active_study.etudeData.synthese.hypotheses_prix,
					property: "logements_sans_parking",
					editable: true,
					suffix: " €/m²",
					restrict_input: "number"
				}
			]);
			table.body.push([
				{ value: "Collectif avec stat." },
				{
					parent_data: this.active_study.etudeData.synthese.hypotheses_prix,
					property: "logements_avec_parking",
					editable: true,
					suffix: " €/m²",
					restrict_input: "number"
				}
			]);

			// <div v-for="(parking, index) in checked_parkings" :key="index">
			//     <el-row-price :parent_data="parking" property="sale_price" :label="parking.label" :editable="true" price_width="150px" format="" />
			// </div>
			for (const parking of this.checked_parkings) {
				let suffix = "€";
				if (!parking.sale_price) {
					suffix = "";
				}
				table.body.push([
					parking.id === "autre" ?
						{
							parent_data: parking,
							property: "label",
							editable: true
						} :
						{
							value: parking.label
						},
					{
						parent_data: parking,
						property: "sale_price",
						editable: true,
						suffix: suffix,
						format: "number",
						restrict_input: "number"
					}
				]);
			}

			if(this.active_study.etudeData.synthese.afficher_individuel_avec_stat) {
				table.body.push([
					{ value: "Individuel avec stat." },
					{
						parent_data: this.active_study.etudeData.synthese.hypotheses_prix,
						property: "individuel_avec_parking",
						editable: true,
						suffix: this.active_study.etudeData.synthese.hypotheses_prix.individuel_avec_parking ? " €" : ""
					}
				]);
			}

			return table;
		},
		table_synthese_locatif () {
			return (index) => this.get_table_synthese_locatif(index);
		},
		checked_parkings () {
			const parking_list = [];
			if (
				_.isUndefined(this.active_study.etudeData.synthese.stationnements_1)
			) {
				return [];
			}
			for (const parking of this.active_study.etudeData.synthese
				.stationnements_1) {
				if (parking.checked) {
					parking_list.push(parking);
				} else if (this.nb_tables > 1) {
					// if checked in 2nd table or 3rd table
					const p2 = _.find(
						this.active_study.etudeData.synthese.stationnements_2,
						["id", parking.id]
					);
					if (p2.checked) {
						parking_list.push(parking);
					} else if (this.nb_tables > 2) {
						// if checked in 2nd table or 3rd table
						const p3 = _.find(
							this.active_study.etudeData.synthese.stationnements_3,
							["id", parking.id]
						);
						if (p3.checked) {
							parking_list.push(parking);
						}
					}
				}
			}
			return parking_list;
		},
		showEvolutions () {
			return this.active_study.etudeType.slug === "actu";
		}
	},
	methods: {
		parking_synthesis (parkings) {
			let recap = "";
			if (
				_.isUndefined(this.active_study.etudeData.synthese.stationnements_1)
			) {
				return "";
			}
			for (const parking of parkings) {
				if (parking.checked) {
					recap += `${parking.label}: ${parking.rent_price} €/mois / `;
				}
			}
			return recap.substring(0, recap.length - 3);
		},
		get_table_synthese_locatif: function (table_n) {
			const table = {
				head: [],
				body: [],
				config: {
					cols_width: [10, 5, 16, 15, 18, 18, 25]
				}
			};

			if (table_n == 1) {
				table.head.push([
					"Type",
					" ",
					"Surf. hab.",
					"Loyer/ mois(HC)",
					"Loyer/ m²",
					"Prix",
					"Prix/ m²",
				]);
			}

			for (let i = 0; i < this.location_lines(table_n); i++) {
				const loc = this.active_study.etudeData.synthese["location_" + table_n][
					i
				];
				let loyer_m2 = number.clean(
					number.clean(loc.loyer_hc_hs) / number.clean(loc.surf_hab)
				);
				if (!loyer_m2) loyer_m2 = "";
				if (loc.prix_unitaire == null){
					this.$store.dispatch("edit_object", {
						obj: loc,
						field: "prix_unitaire",
						value: ""
					});
				}
				let prix_m2 = number.clean(
					number.clean(loc.prix_unitaire * 1000) / number.clean(loc.surf_hab)
				);
				if (!prix_m2) prix_m2 = "";

				table.body.push([
					{ parent_data: loc, property: "type", editable: true },
					{ parent_data: loc, property: "nb", editable: true },
					{
						parent_data: loc,
						property: "surf_hab",
						editable: true,
						suffix: " m²"
					},
					{
						parent_data: loc,
						property: "loyer_hc_hs",
						editable: true,
						suffix: " €"
					},
					{ value: number.format(loyer_m2, 1), suffix: " €" },
					{
						parent_data: loc,
						property: "prix_unitaire",
						editable: true,
						suffix: " k€"
					},
					{ value: number.format(prix_m2, 0), suffix: " €" },
				]);
			}

			const total = { type: 0, nb: 0, surf_hab: 0, loyer_hc_hs: 0, loyer_m2: 0, prix_unitaire: 0, prix_m2: 0};
			for (let i = 0; i < this.location_lines(table_n); i++) {
				const loc = this.active_study.etudeData.synthese["location_" + table_n][i];
				const nb = number.clean(loc.nb);
				if (nb) {
					total.nb += nb;
					total.surf_hab += number.clean(loc.surf_hab) * nb;
					total.loyer_hc_hs += number.clean(loc.loyer_hc_hs) * nb;
					total.loyer_m2 += (number.clean(loc.loyer_hc_hs) / number.clean(loc.surf_hab)) * nb;
					total.prix_unitaire += number.clean(loc.prix_unitaire) * nb;
					total.prix_m2 += (number.clean(loc.prix_unitaire) / number.clean(loc.prix_unitaire)) * nb;
				}
			}
			const mean_surface = total.surf_hab / total.nb;

			const mean_price = total.loyer_hc_hs / total.nb;

			const mean_prix_m_2 = total.prix_unitaire / total.nb;
			table.body.push([
				"Total/ moy.",
				total.nb,
				{ value: number.format(mean_surface), suffix: "m²" },
				{ value: number.format(mean_price), suffix: "€" },
				{ value: number.format(mean_price / mean_surface, 1), suffix: "€" },
				{ value: number.format(mean_prix_m_2), suffix: " k€" },
				{ value: number.format(mean_prix_m_2 * 1000 / mean_surface, 0), suffix: "€" }
			]);

			// arrondi des valeurs affichées à la dizaine pour les prix/m² (dont moyenne)
			const COLUMN_M2_PRICE = 6;
			table.body.forEach(l => {
				if(l[COLUMN_M2_PRICE].value) {
					let value = _.cloneDeep(l[COLUMN_M2_PRICE].value);
					value = Math.round(parseInt(value.replace(/\s/gi, '')) / 10) * 10;
					if (value) value = number.format(value);
					l[COLUMN_M2_PRICE].value = value;
				}
			});

			// arrondi de la moyenne des prix unitaires moyen
			const COLUMN_PRICE = 5;
			let prixUnitaireARemplacer = table.body[table.body.length - 1][COLUMN_PRICE].value;
			if (prixUnitaireARemplacer) {
				const value = Math.round(prixUnitaireARemplacer.match(/\d/gi).join('') / 10) * 10;
				table.body[table.body.length - 1][COLUMN_PRICE].value = number.format(value);
			}

			return table;
		},
		set_location_line (line, value) {
			this.$store.dispatch("edit_object", {
				obj: this.active_study.etudeData.synthese,
				field: `location_${line}_lines`,
				value: parseInt(value)
			});
		}
	},
	mounted: function () {
		if (this.active_study) {
			for (let j = 1; j <= 3; j++) {
				let n = 0;
				for (const i in this.active_study.etudeData.synthese["location_" + j]) {
					if (
						this.active_study.etudeData.synthese["location_" + j][i].type ||
            this.active_study.etudeData.synthese["location_" + j][i].nb ||
            this.active_study.etudeData.synthese["location_" + j][i].surf_hab ||
            this.active_study.etudeData.synthese["location_" + j][i].loyer_hc_hs
					)
						n = number.clean(i);
				}
				if (n > 9) n = 9;
			}
		}
	},
	components: {
		Information
	},
	watch: {
		total_lines: function (new_value) {
			if(new_value > 12)
				this.$store.dispatch("add_notification", { status: "error", message: "Trop de tableaux ou de lignes, il y a un risque de dépassement dans la page" });
		}
	}
};
</script>

<style lang="stylus" scoped>
@import '../../../../../assets/css/app'
@import '../../../../../assets/css/page'

.fixed-margin
  margin-top 4px
  margin-bottom 16px

ul
  list-style-type none !important

.td-cb
  min-width 120px
  width 60%

.td-price
  width 40%

.ss-rappel-prio
  background #eee
  border-radius 3px
  overflow hidden
  font-weight bold

  &__label
    display flex

    >>> .icon
      flex-shrink 0
      margin-right 5px

  .priorite-wrapper
    position relative
    background main_color
    color #fff
    display flex
    flex-direction column
    justify-content center
    padding-left 30px

    &::before
      position absolute
      left -1px
      top 0
      bottom 0
      display block
      width 20px
      content ""
      background no-repeat left/cover
      background-image url("data:image/svg+xml,%0A%3Csvg width='22px' height='84px' viewBox='0 0 22 84' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cpolygon id='Path-2' fill='%23EEEEEE' points='0.5546875 -0.046875 21.5390625 40.953125 0.5546875 83.1780502'%3E%3C/polygon%3E%3C/g%3E%3C/svg%3E")

.priorite
  text-align center
  line-height 1.5
  font-size 18px

  .input
    min-height @line-height * 1
    max-height @line-height * 1
</style>
<style lang="scss">
.error-message {
  display: none;
}
.box-edit.error {
  box-shadow: 0 0 0 2px #f00 !important;
  .error-message {
    display: block;
    color: #F00;
    padding: 6px;
  }
}
</style>
